import { css } from "@emotion/react"
import { Children, useCallback, useEffect, useRef, useState } from "react"
import { Icon } from "../Icon"

const circleButtonStyles = css({
  width: 32,
  height: 32,
  padding: 0,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
})

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false

// The scroll track bleeds to the container's edges via a negative inline
// margin, so it must match the container's inline padding or the boundary
// cards overflow the container.
const INLINE_PADDING = "1rem"

type CardCarouselProps = {
  children: React.ReactNode
}

export function CardCarousel({ children }: CardCarouselProps) {
  const carouselRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollBounds = useCallback(() => {
    const el = carouselRef.current
    if (!el) return

    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  const childCount = Children.count(children)

  useEffect(() => {
    const el = carouselRef.current
    if (!el) return

    updateScrollBounds()
    const observer = new ResizeObserver(updateScrollBounds)
    observer.observe(el)
    return () => observer.disconnect()
  }, [updateScrollBounds, childCount])

  if (childCount === 1) {
    return <div css={{ maxWidth: 350 }}>{Children.toArray(children)[0]}</div>
  }

  const scrollByCard = (direction: number) => {
    const el = carouselRef.current
    if (!el) return

    const gap = parseFloat(getComputedStyle(el).columnGap) || 0
    const cardWidth = el.firstElementChild
      ? el.firstElementChild.clientWidth + gap
      : el.clientWidth

    // Scroll to an absolute, card-aligned position rather than stepping
    // relatively. Resting at the end-aligned last card leaves scrollLeft off
    // the card grid, and relative steps would preserve that offset, so the
    // first card never realigns to the start when navigating back.
    const currentCard = Math.round(el.scrollLeft / cardWidth)

    el.scrollTo({
      left: (currentCard + direction) * cardWidth,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    })
  }

  return (
    <div
      css={{
        borderRadius: 8,
        paddingBlock: INLINE_PADDING,
        paddingInline: INLINE_PADDING,
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        backgroundColor: "var(--d-fill-color-neutral-090)",
        border: "1px solid var(--d-fill-color-neutral-090)",
      }}
    >
      <div
        ref={carouselRef}
        onScroll={updateScrollBounds}
        css={{
          display: "flex",
          gap: INLINE_PADDING,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          // Extend the carousel into the container's horizontal padding
          marginInline: `-${INLINE_PADDING}`,
          paddingInline: INLINE_PADDING,
          scrollPaddingInline: INLINE_PADDING,
          "& > *": {
            flex: "0 0 45%",
            scrollSnapAlign: "start",
            "@media (max-width: 600px)": {
              flex: "0 0 90%",
            },
          },
          // The last card has no start-aligned snap point at the right end, so
          // snap its end edge to the right scroll-padding edge instead. This
          // lets the track rest with the final card flush against the container
          // padding when navigating to the end with the buttons.
          "& > *:last-child": {
            scrollSnapAlign: "end",
          },
          "& > .action-drawer": {
            borderRadius: 8,
          },
        }}
      >
        {children}
      </div>
      {(canScrollLeft || canScrollRight) && (
        <div className="d-f ai-c g-1">
          <button
            type="button"
            className="btn secondary-btn minor-btn"
            css={circleButtonStyles}
            aria-label="Scroll left"
            disabled={!canScrollLeft}
            onClick={() => scrollByCard(-1)}
          >
            <Icon symbol="general#left-chevron" />
          </button>
          <button
            type="button"
            className="btn secondary-btn minor-btn"
            css={circleButtonStyles}
            aria-label="Scroll right"
            disabled={!canScrollRight}
            onClick={() => scrollByCard(1)}
          >
            <Icon symbol="general#right-chevron" />
          </button>
        </div>
      )}
    </div>
  )
}
